(() => {
  'use strict';
  const rules = window.SpaceArmoryFitRules;
  const config = document.querySelector('[data-face-fit-account-config]');
  if (!rules || !config) return;
  // Text in the shopper's language: snippets/sa-i18n-js.liquid sets window.SA_I18N on French, Spanish and German
  // pages only. English pages have no dictionary, so every message falls back to its English text unchanged.
  const accText = (key, fallback) => window.SA_I18N?.[key] ?? fallback;
  const settings = JSON.parse(config.textContent);
  if (!settings.enabled) return;
  const endpoint = '/apps/face-fit/measurement';
  const pendingKey = 'sa_face_fit_login_pending_v1';
  const message = code => ({
    measurement_deviation:accText('account.deviation','This differs substantially from your saved measurement. Please measure again. Your saved measurement is unchanged.'),
    measurement_changed:accText('account.changed_elsewhere','Your saved measurement changed on another page. Review it before saving again.'),
    older_measurement:accText('account.older','A newer measurement is already saved. Please measure again.'),
    measurement_expired:accText('account.expired','Please measure again before saving.'),
    sign_in_required:accText('account.sign_in_required','Please sign in to save your measurement.'),
    invalid_measurement:accText('account.invalid','Please take a new, steady measurement.'),
    account_changed:accText('account.account_changed','Your account changed. Please review the measurement and save again.'),
  }[code] || accText('account.save_failed','Your measurement could not be saved. Please try again.'));
  const api = new EventTarget();
  api.state = {customer:null, measurement:null, revision:0, status:'loading'};
  let requestNumber = 0;
  api.emit = () => api.dispatchEvent(new CustomEvent('change',{detail:api.state}));
  api.request = async (method='GET', body) => {
    const response = await fetch(endpoint, {method, credentials:'same-origin', cache:'no-store',
      headers:{Accept:'application/json', ...(body ? {'Content-Type':'application/json'} : {})},
      ...(body ? {body:JSON.stringify(body)} : {}), signal:AbortSignal.timeout(10000)});
    const data = await response.json();
    if (!response.ok) throw new Error(data.error || 'service_unavailable');
    if (!('customer' in data) || !Number.isSafeInteger(data.revision)) throw new Error('service_unavailable');
    return data;
  };
  api.refresh = async () => {
    const number = ++requestNumber;
    try {
      const data = await api.request();
      if (number === requestNumber) {
        api.state = {...data, status:'ready'};
        if (data.measurement && !rules.valid(data.measurement)) api.state.measurement = null;
        api.emit();
      }
      return api.state;
    } catch (error) {
      if (number === requestNumber) { api.state = {...api.state, status:'error', measurement:null}; api.emit(); }
      throw error;
    }
  };
  api.save = async (record, customer, revision) => {
    if (!customer || customer !== api.state.customer) throw new Error('account_changed');
    if(api.writeBusy)throw new Error('save_in_progress');
    ++requestNumber;
    api.writeBusy=true;
    let data;
    try{data = await api.request('POST',{measurement:record, revision, csrf:api.state.csrf});}finally{api.writeBusy=false;}
    // A response to an earlier identity must never populate a different account.
    if (data.customer !== customer || api.state.customer !== customer) throw new Error('account_changed');
    // Discard reads started during this write: they may contain the previous revision.
    ++requestNumber;
    api.state = {...data,status:'ready'}; api.emit();
    try { localStorage.setItem('sa_face_fit_changed', String(Date.now())); } catch {}
    return api.state;
  };
  window.SpaceArmoryFitAccount = api;
  api.refresh().catch(()=>{});
  window.addEventListener('pageshow',()=>api.refresh().catch(()=>{}));
  window.addEventListener('focus',()=>api.refresh().catch(()=>{}));
  window.addEventListener('storage',event=>{ if(event.key==='sa_face_fit_changed')api.refresh().catch(()=>{}); });
  document.addEventListener('visibilitychange',()=>{if(!document.hidden)api.refresh().catch(()=>{});});

  customElements.define('face-fit-profile',class extends HTMLElement {
    connectedCallback() {
      if(this.abort)return;
      this.abort = new AbortController();
      const signal=this.abort.signal;
      this.root=this.closest('face-fit-scanner');
      this.autoToggle=this.root.querySelector('[data-profile-auto-update]');
      this.autoToggle?.addEventListener('change',()=>{this.autoSave=this.autoToggle.checked;this.render();},{signal});
      this.toggle=this.querySelector('[data-profile-toggle]');
      this.label=this.querySelector('[data-profile-label]');
      this.hint=this.querySelector('[data-profile-hint]');
      this.status=this.querySelector('[data-profile-status]');
      this.login=this.querySelector('[data-profile-login]');
      this.retry=this.querySelector('[data-profile-retry]');
      this.hidden=false;
      this.autoSave=!!api.state.measurement;
      this.customer=api.state.customer;
      this.saveRevision=api.state.revision;
      this.toggle.addEventListener('change',()=>this.change(),{signal});
      this.retry.addEventListener('click',()=>this.save(),{signal});
      this.root.addEventListener('space-armory:scan-complete',event=>{
        if(!rules.valid(event.detail?.measurement))return;
        if(!event.detail.restored)this.clearPending();
        this.record=event.detail.measurement;
        this.customer=api.state.customer;
        this.saveRevision=api.state.revision;
        this.status.textContent='';
        this.login.hidden=true;
        this.saved=false;
        if(!event.detail.restored && this.autoSave && this.customer)this.save();
        else this.render();
      },{signal});
      api.addEventListener('change',()=>{
        const state=api.state;
        if(this.customer && state.customer!==this.customer) {
          this.autoSave=false;this.saved=false;this.clearPending();
          this.status.textContent=accText('account.account_changed_review','Your account changed. Review this measurement before saving.');
        }
        if(state.status==='ready') {
          if(!this.record)this.autoSave=!!state.measurement;
          if(this.saved && state.measurement?.measuredAt!==this.record?.measuredAt){this.saved=false;this.autoSave=false;}
          if(state.status==='ready' && !state.measurement && this.hadSavedProfile)this.autoSave=false;
          this.hadSavedProfile=!!state.measurement;
          this.customer=state.customer;
          this.saveRevision=state.revision;
        }
        this.render();
        this.resume();
      },{signal});
      this.root.addEventListener('space-armory:scanner-ready',()=>this.resume(),{signal});
      this.render();this.resume();
    }
    disconnectedCallback(){this.abort?.abort();this.abort=null;}
    clearPending(){try{sessionStorage.removeItem(pendingKey)}catch{}}
    render(){
      if(this.autoToggle){
        this.autoToggle.closest('[data-profile-auto-row]').hidden=!api.state.measurement || api.state.status!=='ready';
        this.autoToggle.checked=!!this.autoSave;
      }
      this.toggle.disabled=this.busy || !this.record;
      this.toggle.checked=!!(this.saved || this.autoSave);
      this.label.textContent=this.busy?accText('account.saving','Saving…'):this.saved?accText('account.saved_label','Measurement saved'):accText('account.save_label','Save my measurement');
      if(this.hint)this.hint.hidden=!!this.saved;
      this.retry.hidden=!this.failed || this.busy;
      this.setAttribute('aria-busy',String(!!this.busy));
    }
    async change(){
      this.autoSave=this.toggle.checked;this.failed=false;
      // A guest has no saved measurement to keep: switching off just clears the sign-in prompt (owner, 11 Oct 2026).
      if(!this.autoSave){this.saved=false;this.clearPending();this.login.hidden=true;this.status.textContent=api.state.customer?accText('account.no_replace','New measurements will not replace your saved measurement. Remove an existing measurement from My Measurements.'):'';this.render();return;}
      if(api.state.status!=='ready') {
        try{await api.refresh()}catch{this.failed=true;this.autoSave=false;this.status.textContent=accText('account.unavailable','Account saving is temporarily unavailable. Your result is still usable.');this.render();return;}
      }
      this.customer=api.state.customer;this.saveRevision=api.state.revision;
      if(!this.customer){this.signIn();return;}
      await this.save();
    }
    signIn(){
      const controller=this.root.closest('product-info')?.faceFitPurchase;
      const pending={measurement:this.record, path:location.pathname, search:location.search, values:controller?.values, expires:Date.now()+20*60*1000};
      this.pendingRestored=this.record.measuredAt;this.resumeAttempted=false;
      try{sessionStorage.setItem(pendingKey,JSON.stringify(pending))}catch{this.status.textContent=accText('account.keep_open','Keep this page open while signing in. Your browser cannot restore the result after a redirect.');}
      this.login.hidden=false;
      // Use Shopify's supported avatar slot; its click bubbles to the native account trigger.
      const account=this.login.querySelector('shopify-account');
      const returnPath=location.pathname+location.search+'#face-fit-saved';
      account.setAttribute('sign-in-url','/customer_authentication/login?return_to='+encodeURIComponent(returnPath));
      this.status.textContent=accText('account.sign_in_prompt','Sign in to save this measurement. You can continue shopping without saving.');
      this.render();
      const fallback=this.login.querySelector('[data-profile-login-fallback]');
      if(fallback){fallback.href='/customer_authentication/login?return_to='+encodeURIComponent(returnPath);fallback.hidden=!!customElements.get('shopify-account');}
      const open=()=>{if(!this.isConnected || this.login.hidden)return;if(fallback)fallback.hidden=true;account.querySelector('[slot="signed-out-avatar"]').click();};
      if(customElements.get('shopify-account'))open();
      else customElements.whenDefined('shopify-account').then(open);
    }
    async resume(){
      if(this.resuming || this.root.dataset.initialized!=='true' || api.state.status!=='ready')return;
      let pending;try{pending=JSON.parse(sessionStorage.getItem(pendingKey))}catch{}
      if(!pending)return;
      if(pending.expires<Date.now() || !rules.valid(pending.measurement)){this.clearPending();return;}
      if(pending.path!==location.pathname)return;
      this.resuming=true;
      try{
        if(this.pendingRestored!==pending.measurement.measuredAt){
          this.pendingRestored=pending.measurement.measuredAt;
          this.record=pending.measurement;this.autoSave=false;
          const controller=this.root.closest('product-info')?.faceFitPurchase;
          if(controller && pending.values?.length===controller.names.length){controller.values=[...pending.values];controller.writeControls();controller.sync();}
          this.root.dispatchEvent(new CustomEvent('space-armory:restore-measurement',{detail:{measurement:this.record}}));
          if(!api.state.customer){
            this.status.textContent=accText('account.ready_retry','Your measurement is ready. Try signing in again, or continue shopping without saving.');
            this.render();
          }
        }
        if(api.state.customer && !this.resumeAttempted){
          // Retry only on an explicit click after a failure; focus/refresh must not loop writes.
          this.resumeAttempted=true;
          this.customer=api.state.customer;this.saveRevision=api.state.revision;
          await this.save();
        }
      }finally{this.resuming=false;}
    }
    async save(){
      if(this.busy || !rules.valid(this.record))return;
      if(!api.state.customer){this.signIn();return;}
      this.busy=true;this.failed=false;this.render();
      try{
        await api.save(this.record,this.customer,this.saveRevision);
        this.saved=true;this.autoSave=true;this.status.textContent='';
        this.clearPending();this.login.hidden=true;
      }catch(error){
        this.saved=false;this.failed=true;this.autoSave=false;this.status.textContent=message(error.message);
        try {
          await api.refresh();
          if(api.state.customer===this.customer && api.state.measurement?.measuredAt===this.record.measuredAt && api.state.measurement?.cm===this.record.cm){
            this.saved=true;this.failed=false;this.autoSave=true;this.status.textContent=accText('account.saved_done','Measurement saved to your account.');
            this.clearPending();this.login.hidden=true;
          }
        } catch {}
      }
      finally{this.busy=false;this.render();}
    }
  });
})();
